import React, { useState } from 'react';
import {
    checkIfKnownIbanBceCombination,
    formatNumberAddress,
    formatPhone,
    handleDeleteFile,
    handleFileValidation,
    isValidNationalRegisterNumber,
} from './FormHelper';
import AddressPartialForm from './PartialForm/AddressPartialForm';
import ContactPartialForm from './PartialForm/ContactPartialForm';

import { IbanOwnerType } from './Types';
import { useTranslation } from '../../Helpers';
import { WidgetHelper } from '../WidgetHelper';
import { FloatLabel } from '../../Components';
import { validateIndependantForm } from './FormValidator';
import { PrimeIndependantService } from '../../Services';
import { PrimeSuccessWidget } from './PrimeSuccessWidget';
import { IIndependantFormData } from './Types';
import AddressApplicantPartialForm from './PartialForm/AddressApplicantPartialForm';
import SelectSectorWork from '../../Components/SelectSectorWork/SelectSectorWork';
import CheckboxField from './PartialForm/CheckboxField';
import FileUploadField from './PartialForm/FileUploadField';
import { SupportStructureSelector } from './PartialForm';

interface Props {
    language: string;
    translationJson: string;
}

const PrimeIndependantForm = ({ language, translationJson }: Props) => {
    const [data, setData] = useState<IIndependantFormData>({
        aceAttestationFile: null as any,
        addressLocality: '',
        addressLocalityApplicant: '',
        addressNumber: '',
        addressNumberApplicant: '',
        addressPostal: '',
        addressPostalApplicant: '',
        addressStreet: '',
        addressStreetApplicant: '',
        affiliationProofFile: null as any,
        hasAgreedToObligations: false,
        iban: '',
        mail: '',
        mailConfirmation: '',
        name: '',
        nationalRegisterApplicant: '',
        noConvention: false,
        noTremplin: false,
        notIndependentPastTwoYears: false,
        numberCompany: '',
        phone: '',
        phoneConfirmation: '',
        sectorCompany: '',
        supportStructure: '',
        surname: '',
        workCompany: '',
        bic: '',
        isKnownCombinationCompanyIBAN: false,
        ibanOwner: IbanOwnerType.OwnerAsker,
        nationalRegisterApplicantIsValid: false,
        dataAreCorrect: false,
        bankAttestationFile: null as any,
    });

    const [errors, setErrors] = useState<
        Partial<Record<keyof IIndependantFormData, string>>
    >({});
    const [isSubmit, setIsSubmit] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

    const translations = useTranslation(translationJson) as Record<
        string,
        string
    >;

    const borderRadius = {
        borderRadius: '5px',
    } as React.CSSProperties;

    const handleChange = <K extends keyof IIndependantFormData>(
        field: K,
        value: IIndependantFormData[K]
    ) => {
        setData((prev) => ({ ...prev, [field]: value }));
    };

    const ibanRaw = data.iban.replace(/\s/g, '').toUpperCase();
    const isBelgianIban = ibanRaw.startsWith('BE');
    const isIbanOk = ibanRaw.length >= 16; // (or real validation)

    const handleBlurNumberCompany = async () => {
        if (ibanRaw && data.numberCompany && isIbanOk) {
            const isKnown = await checkIfKnownIbanBceCombination(
                ibanRaw,
                data.numberCompany
            );
            setData((prev) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: isKnown,
            }));
        } else {
            setData((prev) => ({
                ...prev,
                isKnownCombinationCompanyIBAN: false,
            }));
        }
    };

    const handleBlurNumberCompanyVoid = () => {
        void handleBlurNumberCompany();
    };

    const handleChangeNumberAddress = (
        value: string,
        field: keyof IIndependantFormData
    ) => {
        const formatted = formatNumberAddress(value);
        setData((prev) => ({ ...prev, [field]: formatted }));
    };

    const handleChangePhone = (
        value: string,
        field: keyof IIndependantFormData
    ) => {
        const phoneFormatted = formatPhone(value);
        setData((prev) => ({ ...prev, [field]: phoneFormatted }));
    };

    const handleSelectSector = (event: { sector: string; work: string }) => {
        setData((prev) => ({
            ...prev,
            sectorCompany: event.sector,
            workCompany: event.work,
        }));
    };

    const handleChangeNationalRegisterNumber = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const input = event.target;

        const match = input.value
            .replace(/\D/g, '')
            .match(/(\d{1,2})(\d{1,2})?(\d{1,2})?(\d{1,3})?(\d{1,2})?/);

        if (match) {
            const [, a, b, c, d, e] = match;

            input.value =
                `${a}` +
                (b ? `.${b}` : '') +
                (c ? `.${c}` : '') +
                (d ? `-${d}` : '') +
                (e ? `.${e}` : '');
        }

        const cleanValue = match ? match.slice(1).join('') : '';
        const isValid = isValidNationalRegisterNumber(cleanValue);

        setData((prev) => ({
            ...prev,
            nationalRegisterApplicant: input.value,
            nationalRegisterApplicantIsValid: isValid,
        }));
    };

    const handleChangeFiles = <K extends keyof IIndependantFormData>(
        e: React.ChangeEvent<HTMLInputElement>,
        name: K
    ) => {
        if (e.target.files && e.target.files[0]) {
            handleFileValidation<IIndependantFormData>(e.target.files[0], {
                fieldName: name,
                setData,
            });
        }
    };

    const onFileDelete = <K extends keyof IIndependantFormData>(name: K) => {
        handleDeleteFile<IIndependantFormData>(name, setData);
    };

    const handleChangeIban = (iban: string) => {
        const val = iban.replace(/\W|_/g, '');
        const letters = val.replace(/\d/g, '').substring(0, 2).toUpperCase();
        let formattedIban = letters;

        if (letters === 'BE' || letters === 'B') {
            const digits = val.replace(/\D/g, '').substring(0, 14);
            formattedIban += digits;
            formattedIban =
                formattedIban.match(/.{1,4}/g)?.join(' ') || formattedIban;
        } else {
            const digits = val.substring(2, 4);
            if (digits.match(/\d{1,2}/)) {
                const otherLetters = val.substring(4, 34).toUpperCase();
                formattedIban += digits + otherLetters;
                formattedIban =
                    formattedIban.match(/.{1,4}/g)?.join(' ') || formattedIban;
            }
        }

        setData((prev) => ({
            ...prev,
            iban: formattedIban,
        }));
    };

    const handleSubmit = () => {
        setHasSubmitted(true);
        void handleSubmitAsync();
    };

    const handleSubmitAsync = async () => {
        const validationErrors = validateIndependantForm(data, translations);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            try {
                if (typeof window.getCaptchaToken !== 'function') {
                    throw new Error('Captcha is not available');
                }

                const captchaToken = await window.getCaptchaToken(
                    'prime_independant_request'
                );

                if (!captchaToken) {
                    throw new Error('Captcha token is empty');
                }

                const response = await PrimeIndependantService.pushData(
                    data,
                    captchaToken
                );

                if (
                    response &&
                    response.status === 200 &&
                    (await response.json()) === true
                ) {
                    setIsSubmit(true);
                    setIsSuccess(true);
                } else {
                    setIsSubmit(true);
                    setIsSuccess(false);
                }
            } catch (error) {
                console.error('Erreur lors de la soumission:', error);
                setIsSubmit(true);
                setIsSuccess(false);
            }
        }
    };

    if (isSubmit) {
        return (
            <PrimeSuccessWidget
                isSuccess={isSuccess}
                dictSuccessText1={translations['Site.Prime.SuccessTextFpie1']}
                dictSuccessText2=""
                dictSuccessContact1={
                    translations['Site.Prime.SuccessContactEmployeur']
                }
                dictSuccessContact2=""
                dictFailLabel={''}
                dictSuccessPhone={''}
                dictSuccessSignature1={''}
                dictSuccessSignature2={''}
            />
        );
    }

    return (
        <form
            id="primeForm"
            data-skip-captcha="true"
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <div className="prime-form-wrapper container">
                <h2 className="h2">
                    {translations['Site.Prime.TitleCompany']}
                </h2>

                <div className="row prime-form-group">
                    <div className="col-12 col-lg-8">
                        <FloatLabel
                            active={!!data.name}
                            label={translations['Site.Prime.Name']}
                            labelId={'name'}
                        >
                            <input
                                type="text"
                                name="name"
                                value={data.name}
                                onChange={(e) =>
                                    handleChange('name', e.target.value)
                                }
                            />
                        </FloatLabel>
                        {hasSubmitted && errors.name && (
                            <div className="row">
                                <label className="alert-danger m-auto">
                                    {translations['Site.Prime.ErrorSubmit']}
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-lg-8">
                        <FloatLabel
                            active={!!data.surname}
                            label={translations['Site.Prime.Surname']}
                            labelId={'surname'}
                        >
                            <input
                                type="text"
                                name="surname"
                                value={data.surname}
                                onChange={(e) =>
                                    handleChange('surname', e.target.value)
                                }
                            />
                        </FloatLabel>
                        {hasSubmitted && errors.surname && (
                            <div className="row">
                                <label className="alert-danger m-auto">
                                    {translations['Site.Prime.ErrorSubmit']}
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-lg-8">
                        <FloatLabel
                            active={!!data.nationalRegisterApplicant}
                            label={
                                translations[
                                    'Site.Prime.NationalRegisterApplicant'
                                ]
                            }
                            labelId={'nationalRegisterApplicant'}
                        >
                            <input
                                type="text"
                                name="nationalRegisterApplicant"
                                value={data.nationalRegisterApplicant}
                                onChange={handleChangeNationalRegisterNumber}
                            />
                        </FloatLabel>
                        {hasSubmitted && errors.nationalRegisterApplicant && (
                            <div className="row">
                                <label className="alert-danger m-auto">
                                    {translations['Site.Prime.ErrorSubmit']}
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                <h3 className="h3">
                    {translations['Site.Prime.AddressApplicant']}
                </h3>

                <AddressApplicantPartialForm
                    values={data}
                    onChange={handleChange}
                    onChangeNumberAddress={(e) =>
                        handleChangeNumberAddress(e, 'addressNumberApplicant')
                    }
                    errors={errors}
                    dictAddressStreetLabel={
                        translations['Site.Prime.AddressStreet']
                    }
                    dictAddressNumberLabel={
                        translations['Site.Prime.AddressNumber']
                    }
                    dictAddressPostalLabel={
                        translations['Site.Prime.AddressPostal']
                    }
                    dictAddressLocalityLabel={
                        translations['Site.Prime.AddressLocality']
                    }
                    dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                />

                <h2 className="h2">
                    {translations['Site.Prime.ExploitationAddressCompany']}
                </h2>

                <AddressPartialForm
                    values={data}
                    onChange={handleChange}
                    onChangeNumberAddress={(e) =>
                        handleChangeNumberAddress(e, 'addressNumber')
                    }
                    errors={errors}
                    dictAddressStreetLabel={
                        translations['Site.Prime.AddressStreet']
                    }
                    dictAddressNumberLabel={
                        translations['Site.Prime.AddressNumber']
                    }
                    dictAddressPostalLabel={
                        translations['Site.Prime.AddressPostal']
                    }
                    dictAddressLocalityLabel={
                        translations['Site.Prime.AddressLocality']
                    }
                    dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                />

                <ContactPartialForm
                    values={data}
                    onChange={handleChange}
                    onChangePhone={handleChangePhone}
                    onChangeIban={(c) => handleChangeIban(c)}
                    onIbanCheck={handleBlurNumberCompanyVoid}
                    errors={errors}
                    dictMailLabel={translations['Site.Prime.MailCompany']}
                    dictMailConfirmationLabel={
                        translations['Site.Prime.MailCompanyConfirmation']
                    }
                    dictPhoneLabel={translations['Site.Prime.PhoneCompany']}
                    dictPhoneConfirmationLabel={
                        translations['Site.Prime.PhoneConfirmation']
                    }
                    dictIbanCompanyLabel={
                        translations['Site.Prime.IbanCompany']
                    }
                    dictBicCompanyLabel={translations['Site.Prime.BicCompany']}
                    dictErrorSubmit={translations['Site.Prime.ErrorSubmit']}
                    dictMailConfirmationError={
                        translations['Site.Prime.MailCompanyConfirmationError']
                    }
                    dictPhoneConfirmationError={
                        translations['Site.Prime.PhoneConfirmationError']
                    }
                    dictIdentificationCodeError={
                        translations['Site.Prime.IdentificationCodeError']
                    }
                    isIbanOk={isIbanOk}
                    isBelgianIban={isBelgianIban}
                />

                <div className="row mt-2">
                    <div className="col-12">
                        <SelectSectorWork
                            language={
                                language.toLowerCase() === 'nl' ? 'nl' : 'fr'
                            }
                            onSelectionChange={handleSelectSector}
                        />

                        {(errors?.sectorCompany || errors?.workCompany) && (
                            <div className="row">
                                <label className="alert-danger m-auto">
                                    {translations['Site.Prime.ErrorSubmit']}
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                <div className="row mt-2">
                    <div className="col-12 col-lg-8">
                        <FloatLabel
                            active={!!data.numberCompany}
                            label={translations['Site.Prime.NumberCompany']}
                            labelId="numberCompany"
                        >
                            <input
                                type="text"
                                name="numberCompany"
                                value={data.numberCompany}
                                onChange={(e) => {
                                    const digits = e.target.value
                                        .replace(/\D/g, '')
                                        .slice(0, 10);

                                    const formatted = digits
                                        ? (digits
                                              .match(/^\d{1,4}|\d{1,3}/g)
                                              ?.join('.') ?? '')
                                        : '';

                                    handleChange('numberCompany', formatted);
                                }}
                                onBlur={handleBlurNumberCompanyVoid}
                            />
                        </FloatLabel>

                        {hasSubmitted && errors.numberCompany && (
                            <div className="row">
                                <label className="alert-danger m-auto">
                                    {translations['Site.Prime.ErrorSubmit']}
                                </label>
                            </div>
                        )}
                    </div>
                </div>
                <h2 className="h2">
                    {translations['Site.Prime.TitlePrimeInformation']}
                </h2>

                <div className="row mt-2">
                    <SupportStructureSelector
                        value={data.supportStructure}
                        onChange={(value: string) =>
                            handleChange('supportStructure', value)
                        }
                        label={translations['Site.Prime.SupportStructureLabel']}
                        placeholder={
                            translations[
                                'Site.Prime.SupportStructureInputPlaceHolder'
                            ]
                        }
                        errorMessage={translations['Site.Prime.ErrorSubmit']}
                    />

                    {errors?.supportStructure && (
                        <div className="row">
                            <label className="alert-danger m-auto">
                                {translations['Site.Prime.ErrorSubmit']}
                            </label>
                        </div>
                    )}

                    <div className="col-12">
                        <h3 className="h3">
                            {translations['Site.Prime.TitleFiles']}
                        </h3>

                        <div className="row px-4 pt-4">
                            <div
                                className={'col-12 pb-4 border'}
                                style={borderRadius}
                            >
                                <div className="row">
                                    <FileUploadField
                                        label={
                                            translations[
                                                'Site.Prime.AceAttestationFile'
                                            ]
                                        }
                                        infoLabel={
                                            translations[
                                                'Site.Prime.AceAttestationFileInfo'
                                            ]
                                        }
                                        file={data.aceAttestationFile}
                                        noneLabel={
                                            translations['Site.Prime.NoneLabel']
                                        }
                                        uploadLabel={
                                            translations['Site.Prime.Upload']
                                        }
                                        error={errors.aceAttestationFile}
                                        errorLabel={
                                            translations[
                                                'Site.Prime.ErrorSubmit'
                                            ]
                                        }
                                        fieldName="aceAttestationFile"
                                        onFileSelect={handleChangeFiles}
                                        onFileDelete={onFileDelete}
                                    />
                                </div>

                                <div className="row">
                                    <FileUploadField
                                        label={
                                            translations[
                                                'Site.Prime.AffiliationProofFile'
                                            ]
                                        }
                                        infoLabel={
                                            translations[
                                                'Site.Prime.AffiliationProofFileInfo'
                                            ]
                                        }
                                        file={data.affiliationProofFile}
                                        noneLabel={
                                            translations['Site.Prime.NoneLabel']
                                        }
                                        uploadLabel={
                                            translations['Site.Prime.Upload']
                                        }
                                        error={
                                            errors.affiliationProofFile &&
                                            translations[
                                                'Site.Prime.ErrorSubmit'
                                            ]
                                        }
                                        fieldName="affiliationProofFile"
                                        onFileSelect={handleChangeFiles}
                                        onFileDelete={onFileDelete}
                                    />
                                </div>

                                <div className="row">
                                    <FileUploadField
                                        label={
                                            translations[
                                                'Site.Prime.BankAttestationFile'
                                            ]
                                        }
                                        infoLabel={
                                            translations[
                                                'Site.Prime.BankAttestationFileInfoIndependant'
                                            ]
                                        }
                                        file={data.bankAttestationFile}
                                        noneLabel={
                                            translations['Site.Prime.NoneLabel']
                                        }
                                        uploadLabel={
                                            translations['Site.Prime.Upload']
                                        }
                                        error={errors.bankAttestationFile}
                                        errorLabel={
                                            translations[
                                                'Site.Prime.ErrorSubmit'
                                            ]
                                        }
                                        fieldName="bankAttestationFile"
                                        onFileSelect={handleChangeFiles}
                                        onFileDelete={onFileDelete}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="checkbox-prime">
                        <CheckboxField
                            id="notIndependentPastTwoYears"
                            name="notIndependentPastTwoYears"
                            label={
                                translations[
                                    'Site.Prime.NotIndependentPastTwoYearsLabel'
                                ]
                            }
                            value={data.notIndependentPastTwoYears}
                            onChange={handleChange}
                            error={
                                errors.notIndependentPastTwoYears &&
                                translations['Site.Prime.ErrorSubmit']
                            }
                            style={borderRadius}
                        />
                        <CheckboxField
                            id="noConvention"
                            name="noConvention"
                            label={translations['Site.Prime.NoConventionLabel']}
                            value={data.noConvention}
                            onChange={handleChange}
                            error={
                                errors.noConvention &&
                                translations['Site.Prime.ErrorSubmit']
                            }
                            style={borderRadius}
                        />
                        <CheckboxField
                            id="noTremplin"
                            name="noTremplin"
                            label={translations['Site.Prime.NoTremplinLabel']}
                            value={data.noTremplin}
                            onChange={handleChange}
                            error={
                                errors.noTremplin &&
                                translations['Site.Prime.ErrorSubmit']
                            }
                            style={borderRadius}
                        />
                        <CheckboxField
                            id="hasAgreedToObligations"
                            name="hasAgreedToObligations"
                            label={
                                translations[
                                    'Site.Prime.HasAgreedToObligationsLabel'
                                ]
                            }
                            value={data.hasAgreedToObligations}
                            onChange={handleChange}
                            error={
                                errors.hasAgreedToObligations &&
                                translations['Site.Prime.ErrorSubmit']
                            }
                            style={borderRadius}
                        />
                        <CheckboxField
                            id="dataAreCorrect"
                            name="dataAreCorrect"
                            label={
                                translations['Site.Prime.DataAreCorrectLabel']
                            }
                            value={data.dataAreCorrect}
                            onChange={handleChange}
                            error={
                                errors.dataAreCorrect &&
                                translations['Site.Prime.ErrorSubmit']
                            }
                            style={borderRadius}
                        />
                    </div>
                </div>

                <div className="col-12 text-right pt-3">
                    <label className="alert-light">
                        {translations['Site.PrimePhoenix.MandatoryField']}
                    </label>
                </div>

                <div className="form-group submit mt-5">
                    <button type="submit" className="btn-block">
                        <i className="icon-check"></i>
                        {translations['Site.FondsRebond.Submit']}
                    </button>
                </div>
            </div>
        </form>
    );
};

WidgetHelper.register(PrimeIndependantForm, 'prime-independant-form-widget');
